import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../services/api";
import { dialogs } from "../context/DialogContext";
import { useReloadOnRouteEnter } from "../hooks/useReloadOnRouteEnter";
import { markListsForRefresh } from "../utils/markListsRefresh";

const FAMILIAS = ["residencial", "comercial", "industrial", "terrenos", "otros"];
const TIPOS_POR_FAMILIA = {
  residencial: ["Piso", "Casa", "Chalet", "Bungalow", "Pareado", "Apartamento"],
  comercial:   ["Local", "Oficina"],
  industrial:  ["Nave", "Almacen"],
  terrenos:    ["Terreno"],
  otros:       ["Garaje", "Trastero"],
};

export default function DemandaDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    nombre: "", familia: "residencial", tipos: [], operacion: "venta",
    poblacion: "", zona: "", precio_min: "", precio_max: "",
    m2_min: "", m2_max: "", habitaciones_min: "", habitaciones_max: "",
    banos_min: "", planta_min: "", planta_max: "",
    garaje: false, trastero: false, terraza: false, piscina: false, ascensor: false,
    notas: "", estado: "activa"
  });
  const [clienteId, setClienteId] = useState(null);
  const [matches, setMatches] = useState([]);
  const [cargandoMatch, setCargandoMatch] = useState(false);
  const [mostrarMatch, setMostrarMatch] = useState(false);

  useReloadOnRouteEnter(() => {
    cargar();
  }, [id]);

  const cargar = async () => {
    try {
      const res = await api.get("/demandas/" + id);
      const d = res.data.data;
      setClienteId(d.cliente_id);
      setForm({
        nombre: d.nombre || "", familia: d.familia || "residencial",
        tipos: d.tipos || [], operacion: d.operacion || "venta",
        poblacion: d.poblacion || "", zona: d.zona || "",
        precio_min: d.precio_min || "", precio_max: d.precio_max || "",
        m2_min: d.m2_min || "", m2_max: d.m2_max || "",
        habitaciones_min: d.habitaciones_min || "", habitaciones_max: d.habitaciones_max || "",
        banos_min: d.banos_min || "", planta_min: d.planta_min || "", planta_max: d.planta_max || "",
        garaje: d.garaje || false, trastero: d.trastero || false, terraza: d.terraza || false,
        piscina: d.piscina || false, ascensor: d.ascensor || false,
        notas: d.notas || "", estado: d.estado || "activa",
      });
    } catch (err) {
      setError("Error cargando la demanda");
    } finally {
      setCargando(false);
    }
  };

  const buscarMatches = async () => {
    setCargandoMatch(true);
    try {
      const res = await api.get("/matching/demanda/" + id);
      setMatches(res.data.data);
      setMostrarMatch(true);
    } catch (err) {
      console.error("Error en matching:", err);
    } finally {
      setCargandoMatch(false);
    }
  };

  const cambiar = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const toggleTipo = (tipo) => {
    setForm(prev => ({
      ...prev,
      tipos: prev.tipos.includes(tipo) ? prev.tipos.filter(t => t !== tipo) : [...prev.tipos, tipo]
    }));
  };

  const guardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      await api.patch("/demandas/" + id, form);
      markListsForRefresh();
      await dialogs.alert("Demanda actualizada correctamente");
    } catch (err) {
      setError(err.response?.data?.error || "Error al guardar");
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async () => {
    if (!(await dialogs.confirm("Seguro que quieres eliminar esta demanda?"))) return;
    try {
      await api.delete("/demandas/" + id);
      await dialogs.alert("Demanda eliminada");
      if (clienteId) navigate("/clientes/" + clienteId);
      else navigate("/clientes");
    } catch (err) {
      setError("Error eliminando demanda");
    }
  };

  if (cargando) return <div className="min-h-screen flex items-center justify-center"><p className="text-gray-400">Cargando...</p></div>;

  const tiposDisponibles = TIPOS_POR_FAMILIA[form.familia] || [];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => clienteId ? navigate("/clientes/" + clienteId) : navigate("/clientes")} className="text-gray-400 hover:text-gray-600">Volver</button>
          <div>
            <h1 className="font-bold text-gray-800 text-lg">{form.nombre}</h1>
            <p className="text-xs text-gray-500">{form.familia} - {form.poblacion}</p>
          </div>
        </div>
        <button onClick={eliminar} className="px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg text-sm font-medium hover:bg-red-100">Eliminar demanda</button>
      </header>
      <main className="max-w-4xl mx-auto p-6">
        <form onSubmit={guardar} className="flex flex-col gap-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="font-bold text-gray-700 text-sm uppercase tracking-wide mb-4">Datos basicos</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2 flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">Nombre de la demanda</label>
                <input name="nombre" value={form.nombre} onChange={cambiar} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">Familia</label>
                <select name="familia" value={form.familia} onChange={cambiar} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500 bg-white">
                  {FAMILIAS.map(f => <option key={f} value={f}>{f.charAt(0).toUpperCase() + f.slice(1)}</option>)}
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">Operacion</label>
                <select name="operacion" value={form.operacion} onChange={cambiar} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500 bg-white">
                  <option value="venta">Venta</option>
                  <option value="alquiler">Alquiler</option>
                  <option value="ambas">Ambas</option>
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">Poblacion</label>
                <input name="poblacion" value={form.poblacion} onChange={cambiar} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">Zona</label>
                <input name="zona" value={form.zona} onChange={cambiar} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">Estado</label>
                <select name="estado" value={form.estado} onChange={cambiar} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500 bg-white">
                  <option value="activa">Activa</option>
                  <option value="pausada">Pausada</option>
                  <option value="cerrada">Cerrada</option>
                </select>
              </div>
            </div>
            {tiposDisponibles.length > 0 && (
              <div className="mt-4">
                <label className="text-sm font-medium text-gray-700 block mb-2">Tipos aceptados</label>
                <div className="flex gap-2 flex-wrap">
                  {tiposDisponibles.map(tipo => (
                    <button key={tipo} type="button" onClick={() => toggleTipo(tipo)} className={"px-3 py-1 rounded-lg text-sm font-medium border transition-all " + (form.tipos.includes(tipo) ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-300 hover:border-blue-400")}>{tipo}</button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="font-bold text-gray-700 text-sm uppercase tracking-wide mb-4">Criterios de busqueda</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">Precio min</label>
                <input name="precio_min" type="number" value={form.precio_min} onChange={cambiar} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">Precio max</label>
                <input name="precio_max" type="number" value={form.precio_max} onChange={cambiar} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">m2 min</label>
                <input name="m2_min" type="number" value={form.m2_min} onChange={cambiar} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">m2 max</label>
                <input name="m2_max" type="number" value={form.m2_max} onChange={cambiar} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">Hab. min</label>
                <input name="habitaciones_min" type="number" value={form.habitaciones_min} onChange={cambiar} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">Hab. max</label>
                <input name="habitaciones_max" type="number" value={form.habitaciones_max} onChange={cambiar} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">Banos min</label>
                <input name="banos_min" type="number" value={form.banos_min} onChange={cambiar} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">Planta min</label>
                <input name="planta_min" type="number" value={form.planta_min} onChange={cambiar} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">Planta max</label>
                <input name="planta_max" type="number" value={form.planta_max} onChange={cambiar} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500" />
              </div>
            </div>
            <div className="mt-4">
              <label className="text-sm font-medium text-gray-700 block mb-2">Extras</label>
              <div className="flex gap-4 flex-wrap">
                {["garaje", "trastero", "terraza", "piscina", "ascensor"].map(extra => (
                  <label key={extra} className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" name={extra} checked={form[extra]} onChange={cambiar} className="w-4 h-4 rounded border-gray-300 text-blue-600" />
                    <span className="text-sm text-gray-700 capitalize">{extra}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="font-bold text-gray-700 text-sm uppercase tracking-wide mb-4">Notas</h2>
            <textarea name="notas" value={form.notas} onChange={cambiar} rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500 resize-none" />
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-gray-700 text-sm uppercase tracking-wide">Inmuebles compatibles</h2>
              <button type="button" onClick={buscarMatches} disabled={cargandoMatch} className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 disabled:opacity-50">{cargandoMatch ? "Buscando..." : "Buscar inmuebles"}</button>
            </div>
            {mostrarMatch && matches.length === 0 && <p className="text-sm text-gray-400">No se encontraron inmuebles compatibles</p>}
            {matches.length > 0 && (
              <div className="flex flex-col gap-2">
                {matches.map(p => (
                  <a key={p.id} href={"/propiedades/" + p.id} style={{textDecoration:"none",color:"inherit"}} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">{p.titulo}</p>
                      <p className="text-xs text-gray-400">{p.tipo} - {p.poblacion || "Sin poblacion"} - {p.precio ? Number(p.precio).toLocaleString("es-ES") + " EUR" : ""}</p>
                      <p className="text-xs text-gray-400">{p.m2_construidos ? p.m2_construidos + " m2" : ""} {p.recamaras ? "- " + p.recamaras + " hab" : ""} {p.banos ? "- " + p.banos + " banos" : ""}</p>
                    </div>
                    <span className="text-xs px-2 py-1 rounded-full font-semibold bg-green-100 text-green-700">{p.referencia || "Ver"}</span>
                  </a>
                ))}
              </div>
            )}
          </div>
          {error && <div className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 rounded-lg">{error}</div>}
          <div className="flex gap-3 justify-end">
            <button type="button" onClick={() => clienteId ? navigate("/clientes/" + clienteId) : navigate("/clientes")} className="px-6 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50">Cancelar</button>
            <button type="submit" disabled={guardando} className="px-6 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">{guardando ? "Guardando..." : "Guardar cambios"}</button>
          </div>
        </form>
      </main>
    </div>
  );
}